<template>
  <button ref="button" v-tooltip="title" :data-primary="primary || undefined" :type="type" :disabled="disabled || loading" :class="buttonClass">
    <BaseSpinner v-if="loading" class="mr-2" size="sm" />
    <svgicon v-if="icon && !loading" :name="icon" size="sm" :class="{ 'mr-1': $slots.default }" />
    <slot />
    <kbd v-if="showShortcutHint" class="kbd-hint" :data-modifier="shortcutHintModifier" aria-hidden="true"><span class="kbd-hint-enter"></span></kbd>
  </button>
</template>

<script>
import { cva } from "class-variance-authority";
import { withRenderContextMixin } from "~/mixins/withRenderContextMixin";
import { cn } from "~/utils/cn";

const buttonVariants = cva(
  "inline-flex items-center whitespace-nowrap rounded-md border font-medium transition-colors duration-150 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 disabled:cursor-default",
  {
    variants: {
      intent: {
        default: "border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400",
        primary: "border-blue-700 bg-blue-700 text-white hover:bg-blue-800 disabled:border-blue-200 disabled:bg-blue-200 disabled:text-white disabled:hover:bg-blue-200",
        secondary: "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:text-blue-400",
        softDanger: "border-red-200 bg-red-50 text-red-600 hover:bg-red-100 disabled:text-red-300",
        danger: "border-red-700 bg-red-700 text-white hover:bg-red-800 disabled:text-red-400",
      },
      size: {
        xs: "h-6 px-2 text-xs leading-6",
        sm: "h-7 px-2 text-xs leading-7",
        md: "h-8 px-3 text-sm leading-8",
        lg: "h-10 px-4 text-sm leading-10",
      },
      appearance: {
        default: "",
        ghost: "",
      },
    },
    defaultVariants: {
      intent: "default",
      size: "md",
      appearance: "default",
    },
  }
);

export default {
  mixins: [withRenderContextMixin()],

  props: {
    type: {
      type: String,
      default: "button",
    },

    size: {
      type: String,
      validator(value) {
        return ["xs", "sm", "md", "lg"].includes(value);
      },
    },

    icon: String,
    title: String,
    loading: Boolean,
    disabled: Boolean,
    primary: Boolean,
    secondary: Boolean,
    softDanger: Boolean,
    danger: Boolean,

    appearance: {
      type: String,
      default: "default",
      validator(value) {
        return ["default", "ghost"].includes(value);
      },
    },
  },

  computed: {
    shortcutHintModifier() {
      return navigator.platform.startsWith("Mac") ? "⌘" : "Ctrl";
    },

    showShortcutHint() {
      if (!["Mac", "Win", "Linux"].some((platform) => navigator.platform.startsWith(platform))) return false;
      // Modal footer wires Cmd/Ctrl+Enter to the primary button.
      if (this.renderContext.includes("modal-footer") && this.primary) return true;
      // Dialog wires Cmd/Ctrl+Enter to the confirm button, which is
      // primary in info dialogs and danger in confirm-delete dialogs.
      if (this.renderContext.includes("dialog-actions") && (this.primary || this.danger)) return true;
      return false;
    },

    computedSize() {
      if (this.size) return this.size;
      if (this.renderContext.includes("modal")) return "lg";
      return "md";
    },

    intent() {
      if (this.primary) return "primary";
      if (this.secondary) return "secondary";
      if (this.softDanger) return "softDanger";
      if (this.danger) return "danger";
      return "default";
    },

    buttonClass() {
      return cn(buttonVariants({ intent: this.intent, size: this.computedSize, appearance: this.appearance }));
    },
  },

  methods: {
    focus() {
      return this.$refs.button.focus();
    },
  },
};
</script>
